import { useEffect, useMemo, useState } from 'react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../utils/createScopedI18n';
import { tasksI18n } from './i18n';
import {
  changeTaskStatus,
  addCommentWithAttachment,
  downloadFile,
} from '../../services/TaskService';
import { useAssignDocumentToTask } from '../../hooks/task/useAssignDocumentToTask';
import { useTaskHtml } from '../../hooks/task/useTaskHtml';
import { TinyMCE_ApiKey } from '../../config';
import { Editor } from '@tinymce/tinymce-react';
import { useUpdateTaskHtml } from '../../hooks/task/useUpdateTaskHtml';
import { usePublishTaskDocument } from '../../hooks/task/usePublishTaskDocument';
import Tag from '../../components/Tag/Tag';
import TaskConfirmation from '../../components/TaskConfirmation/TaskConfirmation';
import FloatingAlert from 'components/Alert/FloatingAlert';
import {
  downloadCommentTaskDoc,
  uploadCommentTaskDoc,
} from 'services/commentTaskDocService';
import { useNavigate, Link } from 'react-router-dom';
import {
  getStoredComments,
  removeStoredComment,
  storeComment,
} from 'utils/commentHistory';
import {
  APPROVAL_STATUS,
  NODE_TYPE,
  WORKFLOW_STATUS,
  invalidateWorkflowReferenceCache,
} from '../../services/workflowService';
import usePermissionsTasks from 'permissions/modules/tasks/usePermissionsTasks';

const DetailTaskModal = ({
  DesignSystemComponents,
  show,
  value,
  onClose,
  currentUserId,
  onRemoveUser,
  asignedUser,
  setAsignedUser,
}) => {
  const { Modal, Badge, Button, SvgIcon, IconButton } = DesignSystemComponents;

  const navigate = useNavigate();

  const { language } = useLanguage();
  const tasksLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: tasksI18n });

  useEffect(() => {
    tasksI18n.changeLanguage(tasksLanguage);
  }, [tasksLanguage]);

  const [newComment, setNewComment] = useState('');
  const [selectedFile, setSelectedFile] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  const editorRef = useRef(null);
  const [showSendWarning, setShowSendWarning] = useState(false);
  const [isLoadingTaskApproved, setIsLoadingTaskApproved] = useState(false);
  const [confirmationMode, setConfirmationMode] = useState(null); // 'approve' | 'reject' | null
  const [errorStatusChange, setErrorStatusChange] = useState(null);
  const { assignDocumentToTask } = useAssignDocumentToTask();
  const [isSavingHtml, setIsSavingHtml] = useState(false);
  const [saveHtmlError, setSaveHtmlError] = useState(null);
  const [saveHtmlSuccess, setSaveHtmlSuccess] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [baselineHtml, setBaselineHtml] = useState('');
  const [recentComments, setRecentComments] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const {
    canPublishComment,
    canAttachFileToComment,
    canGoToAssociatedCase,
    canChangeStatus,
    canEditDocument,
    canRemoveAssignedPerson,
  } = usePermissionsTasks();

  const { html, setHtml, isLoadingHtml, htmlError, getTaskHtml } =
    useTaskHtml();

  const { updateHtml } = useUpdateTaskHtml();

  const { publish } = usePublishTaskDocument();

  const statusIdMap = {
    approved: 1,
    in_progress: 2,
    rejected: 4,
    unassigned: 5,
  };

  const parseEnumNumber = (statusValue) => {
    if (typeof statusValue === 'number') return statusValue;
    if (typeof statusValue !== 'string') return null;

    const normalized = statusValue.trim().toLowerCase();
    if (normalized === 'task') return NODE_TYPE.TASK;
    if (normalized === 'approval') return NODE_TYPE.APPROVAL;
    if (normalized === 'pending') return APPROVAL_STATUS.PENDING;
    if (normalized === 'approved') return APPROVAL_STATUS.APPROVED;
    if (normalized === 'rejected') return APPROVAL_STATUS.REJECTED;
    if (normalized === 'inprogress') return WORKFLOW_STATUS.IN_PROGRESS;
    if (normalized === 'finalized') return WORKFLOW_STATUS.FINALIZED;

    const asNumber = Number(statusValue);
    return Number.isFinite(asNumber) ? asNumber : null;
  };

  const normalizeStatusToken = (rawValue) =>
    String(rawValue || '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, '_');

  const effectiveStatusKey = useMemo(() => {
    const rawStatus = normalizeStatusToken(value?.estado);
    if (!rawStatus) {
      return '';
    }

    if (
      rawStatus === 'completada' ||
      rawStatus === 'aprobada' ||
      rawStatus === 'approved'
    ) {
      return 'approved';
    }

    if (
      rawStatus === 'en_progreso' ||
      rawStatus === 'in_progress' ||
      rawStatus === 'inprogress'
    ) {
      return 'in_progress';
    }

    if (rawStatus === 'rechazada' || rawStatus === 'rejected') {
      return 'rejected';
    }

    if (rawStatus === 'sin_asignar' || rawStatus === 'unassigned') {
      return 'unassigned';
    }

    return rawStatus;
  }, [value?.estado]);

  const statusBadgeConfig = useMemo(() => {
    if (effectiveStatusKey === 'approved') {
      return { text: 'Aprobada', color: 'success' };
    }

    if (effectiveStatusKey === 'in_progress') {
      return { text: 'En progreso', color: 'warning' };
    }

    if (effectiveStatusKey === 'rejected') {
      return { text: 'Rechazada', color: 'error' };
    }

    if (effectiveStatusKey === 'unassigned') {
      return { text: 'Sin asignar', color: 'info' };
    }

    return {
      text: value?.estado || '--',
      color: 'info',
    };
  }, [effectiveStatusKey, value?.estado]);

  const approvalGuard = useMemo(() => {
    const workflowInstance = value?.workflowInstance;
    if (!workflowInstance) {
      return { canApprove: true, reason: null };
    }

    const workflowStatus = parseEnumNumber(workflowInstance.status);
    if (workflowStatus !== WORKFLOW_STATUS.IN_PROGRESS) {
      return { canApprove: true, reason: null };
    }

    const nodes = Array.isArray(workflowInstance.nodes)
      ? workflowInstance.nodes
      : [];

    const activeApprovalNodes = nodes.filter((node) => {
      const nodeType = parseEnumNumber(node?.nodeType);
      const nodeStatus = parseEnumNumber(node?.status);
      const approvalStatus = parseEnumNumber(node?.approvalStatus);

      return (
        nodeType === NODE_TYPE.APPROVAL &&
        nodeStatus === 2 &&
        approvalStatus === APPROVAL_STATUS.PENDING
      );
    });

    if (activeApprovalNodes.length === 0) {
      return {
        canApprove: false,
        reason: t('detail_error_no_active_approval'),
      };
    }

    const approverUserIds = activeApprovalNodes
      .map(
        (node) =>
          Number(node?.requiredApproverUserId) ||
          Number(node?.approverUserId) ||
          null
      )
      .filter((id) => Number.isFinite(id) && id > 0);

    if (approverUserIds.length === 0) {
      return {
        canApprove: false,
        reason: t('detail_error_no_approver_defined'),
      };
    }

    const currentId = Number(currentUserId) || null;
    if (!currentId || !approverUserIds.includes(currentId)) {
      return {
        canApprove: false,
        reason: t('detail_error_not_approver', { id: approverUserIds[0] }),
      };
    }

    return { canApprove: true, reason: null };
  }, [currentUserId, value?.workflowInstance]);

  useEffect(() => {
    if (show && value?.id) {
      getTaskHtml(value.id);
      setIsDirty(false);
      editorRef.current?.setDirty(false);
    }
  }, [show, value?.id, getTaskHtml]);

  useEffect(() => {
    if (!show) {
      setShowSendWarning(false);
    }
  }, [show]);

  const isSacOrEepp =
    !value?.documenTypeBusinessAreaId ||
    value?.documenTypeBusinessAreaId === 2 ||
    value?.documenTypeBusinessAreaId === 11;

  const shouldShowPjudWarning =
    value?.tipoEnvio === 2 && value?.templateId && !isSacOrEepp;

  const handleStatusChange = async (statusKey) => {
    setErrorStatusChange(null);
    const isApprovalDecision =
      statusKey === 'approved' || statusKey === 'rejected';

    if (isApprovalDecision && !approvalGuard.canApprove) {
      setErrorStatusChange(
        approvalGuard.reason || t('detail_error_no_permission')
      );
      return;
    }

    const statusId = statusIdMap[statusKey];
    if (!statusId || !value?.id || !currentUserId) {
      console.error('Faltan datos para cambiar el estado:', {
        statusId,
        taskId: value?.id,
        currentUserId,
      });
      setErrorStatusChange(t('detail_error_missing_data'));
      return;
    }

    try {
      setIsLoadingTaskApproved(true);

      const hasHtml = html && html.trim().length > 0;

      if (hasHtml && (statusKey === 'approved' || statusKey === 'rejected')) {
        const updateResult = await updateHtml(value.id, html);
        if (!updateResult?.ok) {
          console.warn('No se pudo actualizar el HTML antes de publicar.');
          throw new Error(t('detail_error_save_doc'));
        }

        const publishResult = await publish(value.id);
        if (!publishResult?.ok) {
          console.warn('No se pudo publicar el documento.');
          throw new Error(t('detail_error_publish_doc'));
        }

        if (
          statusKey === 'approved' &&
          value.documentConfigurationId &&
          !isSacOrEepp
        ) {
          try {
            await assignDocumentToTask({
              documentConfigurationId: value.documentConfigurationId,
              taskId: value.id,
              userId: currentUserId,
              createdAt: new Date()
                .toLocaleString('sv-SE', { timeZone: 'America/Santiago' })
                .replace(' ', 'T'),
            });
          } catch (assignError) {
            console.warn(
              'No se pudo asociar el documento a la tarea:',
              assignError
            );
            throw new Error(t('detail_error_assign_doc'));
          }
        }
      }

      const tasksDoc = {};
      await changeTaskStatus(value.id, statusId, currentUserId, tasksDoc);
      invalidateWorkflowReferenceCache();

      editorRef.current?.setDirty(false);
      setIsDirty(false);

      onClose();
    } catch (error) {
      console.error('Error al cambiar el estado:', error);
      const backendMessage =
        error?.response?.data?.message ||
        (typeof error?.response?.data === 'string'
          ? error.response.data
          : null);
      setErrorStatusChange(
        backendMessage ||
          (error instanceof Error
            ? error.message
            : t('detail_error_status_change'))
      );
    } finally {
      setShowSendWarning(false);
      setIsLoadingTaskApproved(false);
    }
  };

  const handlePostComment = async () => {
    if (!newComment) {
      alert(t('detail_alert_comment_required'));
      return;
    }
    // Habilita si hay texto O si hay un archivo
    if (
      (!newComment && selectedFile.length === 0) ||
      !value?.id ||
      !currentUserId
    )
      return;

    setIsSubmitting(true);
    try {
      // Llama al servicio unificado que convierte a Base64
      const resp = await addCommentWithAttachment(
        value.id,
        newComment,
        currentUserId,
        null // importante: comentario sin archivo
      );

      // Subir N archivos al comentario
      if (selectedFile.length > 0 && resp) {
        await uploadCommentTaskDoc({
          commentTaskId: resp,
          creatorId: currentUserId,
          caseId: value.caseId,
          files: selectedFile,
        });
      }

      if (resp) {
        storeComment(newComment);
        setRecentComments(getStoredComments());
      }

      setNewComment('');
      setSelectedFile([]);
      onClose(); // Cierra y recarga
    } catch (error) {
      console.error('Error al publicar comentario:', error);
      // (Aquí deberías mostrar un error al usuario)
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- Handler para el click en "Adjuntar" ---
  const handleAttachFileClick = () => {
    fileInputRef.current.click(); // Abre el diálogo de archivo
  };

  // --- Handler para cuando se selecciona un archivo ---
  const handleFileSelected = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      setSelectedFile((prev) => [...prev, ...files]);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  const closeModalConfirmate = () => {
    setShowSendWarning(false);
  };

  const getFileIcon = (name) => {
    if (!name) return 'fileText';
    if (name.endsWith('.pdf')) return 'filePdf';
    if (name.endsWith('.doc') || name.endsWith('.docx')) return 'fileWord';
    return 'fileText';
  };

  const handleDownloadFile = async (comentario) => {
    try {
      await downloadFile({
        tipoComentario: 1,
        commentableId: comentario.id,
      });
    } catch (error) {
      console.error('Error al descargar archivo: ', error);
    }
  };

  const handleDownloadCommentTaskDoc = async (comentario) => {
    try {
      await downloadCommentTaskDoc({
        tipoComentario: 1,
        commentableId: comentario.id,
      });
    } catch (error) {
      console.error('Error al descargar archivo: ', error);
    }
  };

  const handleSaveHtmlOnly = async () => {
    setSaveHtmlError(null);
    setSaveHtmlSuccess(null);

    if (!value?.id) {
      setSaveHtmlError(t('detail_error_missing_task_id'));
      return;
    }

    const hasHtml = html && html.trim().length > 0;
    if (!hasHtml) {
      setSaveHtmlError(t('detail_error_no_html'));
      return;
    }

    try {
      setIsSavingHtml(true);
      const updateResult = await updateHtml(value.id, html);

      if (!updateResult?.ok) {
        throw new Error(t('detail_error_save_doc'));
      }

      setSaveHtmlSuccess(t('detail_success_save_doc'));
      setBaselineHtml(html);
      setIsDirty(false);
      editorRef.current?.setDirty(false);
    } catch (err) {
      setSaveHtmlError(
        err instanceof Error ? err.message : t('detail_error_save_doc')
      );
    } finally {
      setIsSavingHtml(false);
    }
  };

  const onCloseModal = () => {
    setSelectedFile([]);
    onClose();
  };

  useEffect(() => {
    if (!show) return;

    const firstAssigned =
      value?.abogadosAsignados?.length > 0
        ? value.abogadosAsignados[0].name
        : '';

    setAsignedUser(firstAssigned);
  }, [show, value?.abogadosAsignados, setAsignedUser]);

  useEffect(() => {
    if (show) {
      setRecentComments(getStoredComments());
    }
  }, [show]);

  return (
    <Modal
      show={show}
      onClose={onCloseModal}
      title={
        <div className='d-flex align-items-center'>
          <h2 className='me-2'>{t('detail_modal_title')}</h2>
          <p className='task-id'>({value?.id})</p>
        </div>
      }
      width='1000px'
    >
      {errorStatusChange && (
        <FloatingAlert
          message={errorStatusChange}
          type='danger'
          onClose={() => setErrorStatusChange(null)}
        />
      )}
      {saveHtmlError && (
        <FloatingAlert
          message={saveHtmlError}
          type='danger'
          onClose={() => setSaveHtmlError(null)}
        />
      )}

      {saveHtmlSuccess && (
        <FloatingAlert
          message={saveHtmlSuccess}
          type='success'
          onClose={() => setSaveHtmlSuccess(null)}
        />
      )}
      {/* <div> {JSON.stringify(value)} </div> */}
      <input
        type='file'
        ref={fileInputRef}
        style={{ display: 'none' }} // <<< ¡Esto lo oculta!
        multiple
        onChange={handleFileSelected}
      />
      {!value ? (
        <div className='text-center'>{t('detail_loading')}</div>
      ) : (
        <>
          <div className='row'>
            <div className='col-9 line-end'>
              <h6 className=''>{value.titulo || '-'}</h6>
              <div className='description-container'>
                <p className='body  mb-3'>{value.descripcion || '-'}</p>
              </div>

              {isLoadingHtml && (
                <p className='caption mb-2'>{t('detail_loading_doc')}</p>
              )}

              {!isLoadingHtml && html && (
                <div className='mb-3'>
                  <h6 className='mb-2'>{t('detail_generated_doc')}</h6>
                  <Button
                    label={isSavingHtml ? t('detail_btn_saving') : t('detail_btn_save_doc')}
                    variant='secondary'
                    size='sm'
                    onClick={handleSaveHtmlOnly}
                    disabled={
                      !isDirty ||
                      isSavingHtml ||
                      isLoadingTaskApproved ||
                      effectiveStatusKey === 'rejected' ||
                      effectiveStatusKey === 'approved' ||
                      !canEditDocument
                    }
                    className='mb-3'
                  />
                  <Editor
                    apiKey={TinyMCE_ApiKey}
                    onInit={(evt, editor) => {
                      editorRef.current = editor;
                      editor.setDirty(false);
                    }}
                    value={html}
                    onEditorChange={(content) => {
                      setHtml(content);
                      setIsDirty(content !== baselineHtml);
                    }}
                    init={{
                      height: '65vh',
                      menubar: true,
                      plugins:
                        'advlist autolink lists link charmap preview anchor searchreplace visualblocks code fullscreen insertdatetime table code help wordcount',
                      toolbar:
                        'undo redo | bold italic underline | alignleft aligncenter alignright | bullist numlist outdent indent | table | removeformat',
                      branding: false,
                      language: 'es',
                    }}
                    disabled={!canEditDocument}
                  />
                </div>
              )}

              {!isLoadingHtml && htmlError && (
                <p className='caption text-danger'>
                  {t('detail_error_load_html')}
                </p>
              )}

              <div className='row'>
                <div className='col-12'>
                  <h6 className=' mt-3'>{t('detail_comments_title')}</h6>
                  <div className='comment-list'>
                    {value.comentarios && value.comentarios.length > 0 ? (
                      value.comentarios.map((comentario, index) => (
                        <div key={index} className='d-flex py-3'>
                          <SvgIcon
                            name='personCircle'
                            size='1.5rem'
                            fill='var(--dark)'
                          />
                          <div className='ms-2'>
                            <div className='d-flex'>
                              <h6 className='me-3'>
                                {comentario.creadorNombre || t('detail_comment_system')}
                              </h6>
                              <p className='caption font-light'>
                                {comentario.fechaCreacion
                                  ? new Date(
                                      comentario.fechaCreacion
                                    ).toLocaleString()
                                  : t('detail_comment_no_date')}
                              </p>
                            </div>
                            <p className='body mb-1'>{comentario.contenido}</p>
                            {/* 1 archivo */}
                            {comentario.urlAdjunto && (
                              <div
                                className='d-flex align-items-center mt-1 cursor-pointer'
                                onClick={() => handleDownloadFile(comentario)}
                              >
                                <SvgIcon
                                  name='fileText'
                                  size='1rem'
                                  className='me-2'
                                  stroke='var(--primary)'
                                />
                                <span className='caption text-primary text-decoration-underline'>
                                  {comentario.nombreAdjunto}
                                </span>
                              </div>
                            )}

                            {/* N archivos */}
                            {comentario.documents?.length > 0 &&
                              comentario.documents.map((doc) => (
                                <div
                                  key={doc.id}
                                  className='d-flex align-items-center mt-1 cursor-pointer'
                                  onClick={() =>
                                    handleDownloadCommentTaskDoc(doc)
                                  }
                                >
                                  <SvgIcon
                                    name='fileText'
                                    size='1rem'
                                    className='me-2'
                                    stroke='var(--primary)'
                                  />
                                  <span className='caption text-primary text-decoration-underline'>
                                    {doc.nameAttachment}
                                  </span>
                                </div>
                              ))}
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className='caption'>{t('detail_no_comments')}</p>
                    )}
                  </div>
                  {/* --- 7. (NUEVO) INPUT PARA AGREGAR COMENTARIO --- */}
                  <div className='new-comment-section mt-3'>
                    <label className='form-label mb-2'>
                      {t('detail_add_comment_label')}
                    </label>
                    <div className='position-relative'>
                      {showSuggestions && recentComments.length > 0 && (
                        <>
                          <div className='task-comment-suggestions--up position-absolute'>
                            {recentComments.map((comment, idx) => (
                              <div
                                key={idx}
                                className='task-comment-suggestion-row'
                              >
                                <button
                                  type='button'
                                  className='task-comment-suggestion d-flex align-items-center justify-content-between'
                                  onClick={() => {
                                    setNewComment(comment);
                                    setShowSuggestions(false);
                                  }}
                                >
                                  {comment}
                                  <IconButton
                                    ariaLabel={t('detail_aria_delete_suggestion')}
                                    icon='close'
                                    size='sm'
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      removeStoredComment(comment);
                                      setRecentComments(getStoredComments());
                                    }}
                                    iconStroke='var(--primary-500)'
                                    className='ms-3'
                                  />
                                </button>
                              </div>
                            ))}
                          </div>
                          <div className='task-comment-suggestions-arrow--up' />
                        </>
                      )}
                      <textarea
                        className='form-control'
                        placeholder={t('detail_comment_placeholder')}
                        rows={5}
                        value={newComment}
                        onChange={(e) => {
                          setNewComment(e.target.value);
                          setShowSuggestions(true);
                        }}
                        onFocus={() => setShowSuggestions(true)}
                        onBlur={() => {
                          setTimeout(() => setShowSuggestions(false), 150);
                        }}
                        disabled={!canPublishComment}
                      />
                    </div>
                    <div className='d-flex mt-2 justify-content-end align-items-center mt-3'>
                      <div className='d-flex align-items-center me-3'>
                        {selectedFile.length > 0 && (
                          <div className='d-flex flex-column me-3'>
                            {selectedFile.map((file, idx) => (
                              <p key={idx} className='caption mb-0'>
                                {file.name}
                              </p>
                            ))}
                          </div>
                        )}

                        {selectedFile.length > 0 && (
                          <IconButton
                            ariaLabel={t('detail_aria_delete_attachment')}
                            icon='close'
                            onClick={() => setSelectedFile([])}
                            className='mt-1'
                            size='md'
                          />
                        )}
                      </div>
                      <Button
                        iconEnd='paperclip'
                        label={
                          isSubmitting ? t('detail_btn_loading') : t('detail_btn_attach')
                        }
                        variant='secondary'
                        size='sm'
                        onClick={handleAttachFileClick}
                        disabled={isSubmitting || !canAttachFileToComment}
                        className='me-3'
                      />
                      <Button
                        label={isSubmitting ? t('detail_btn_publishing') : t('detail_btn_publish')}
                        variant='primary'
                        onClick={handlePostComment}
                        disabled={
                          (!newComment && !selectedFile) ||
                          isSubmitting ||
                          !canPublishComment
                        }
                        size='sm'
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className='col-3'>
              <div className='mb-3'>
                <p className='mini mb-1 font-light'>{t('detail_status_label')}</p>
                <Badge
                  text={
                    value.estado === 'Completada' || value.estado === 'approved'
                      ? t('detail_status_approved')
                      : value.estado === 'in_progress'
                        ? t('detail_status_in_progress')
                        : value.estado === 'rejected'
                          ? t('detail_status_rejected')
                          : value.estado === 'unassigned'
                            ? t('detail_status_unassigned')
                            : value.estado
                  }
                  color={
                    value.estado === 'Completada' || value.estado === 'approved'
                      ? 'success'
                      : value.estado === 'in_progress'
                        ? 'warning'
                        : value.estado === 'rejected'
                          ? 'error'
                          : 'info'
                  }
                  size='sm'
                />
              </div>
              <div className='mb-3'>
                <p className='mini mb-1 font-light'>{t('detail_priority_label')}</p>
                <p className='caption mb-0'>
                  {value.priorityId === 1
                    ? t('filter_priority_high')
                    : value.priorityId === 2
                      ? t('filter_priority_medium')
                      : value.priorityId === 3
                        ? t('filter_priority_low')
                        : '-'}
                </p>
              </div>
              <div className='mb-3'>
                <p className='mini mb-1 font-light'>{t('detail_client_label')}</p>
                <p
                  className='caption font-blue mb-0 cursor-pointer'
                  onClick={() =>
                    navigate(`/crm/detalles-cliente/${value.clientId}`)
                  }
                >
                  {value.clientName}
                </p>
              </div>
              <div className='mb-3'>
                <p className='mini mb-1 font-light'>{t('detail_rut_label')}</p>
                <p className='caption mb-0'>{value.clientRut}</p>
              </div>
              {/* <div className='mb-3'>
                <p className='mini mb-1 font-light'>Tipo de creación</p>
                <Badge
                  text={value.tipoCreacion || 'No definido'}
                  color={
                    value.tipoCreacion === 'Automática'
                      ? 'info'
                      : value.tipoCreacion === 'Monitor'
                        ? 'warning'
                        : 'light'
                  }
                  size='sm'
                />
              </div> */}
              <div className='mb-3'>
                <p className='mini mb-1 font-light'>{t('detail_case_label')}</p>
                {!value?.businessAreaId ||
                value.businessAreaId === 2 ||
                value.businessAreaId === 11 ||
                value.casoAsociado === '' ||
                !canGoToAssociatedCase ? (
                  <p className='caption'>--</p>
                ) : (
                  <Link
                    to={`/detalle-causa/${value.caseId}/movimientos`}
                    className='text-decoration-none'
                  >
                    <Button
                      label={value.casoAsociado || t('detail_case_no_rol')}
                      variant='naked'
                      size='sm'
                    />
                  </Link>
                )}
              </div>
              <div className='mb-3'>
                <p className='mini mb-1 font-light'>{t('detail_assigned_label')}</p>
                <div className='d-flex justify-content-between align-items-center mb-1'>
                  <p className='caption mb-0'>{asignedUser || t('detail_unassigned')}</p>
                  {asignedUser && (
                    <IconButton
                      ariaLabel={t('detail_aria_remove_user')}
                      icon='close'
                      onClick={async () => {
                        await onRemoveUser(
                          value,
                          value.abogadosAsignados[0].id
                        );
                        handleStatusChange('unassigned');
                      }}
                      size='sm'
                      disabled={!canRemoveAssignedPerson}
                    />
                  )}
                </div>
              </div>
              <div className='mb-3'>
                <p className='mini mb-1 font-light'>{t('detail_created_date_label')}</p>
                <p className='caption mb-0'>
                  {value.fechaCreacion
                    ? new Date(value.fechaCreacion).toLocaleDateString()
                    : '-'}
                </p>
              </div>
              <div className='mb-3'>
                <p className='mini mb-1 font-light'>{t('detail_due_date_label')}</p>
                <p className='caption mb-0'>
                  {value.fechaVencimiento
                    ? new Date(value.fechaVencimiento).toLocaleDateString()
                    : '-'}
                </p>
              </div>
              <div>
                <div className='row mb-3 pt-3 border-top'>
                  <div className='col-12'>
                    <h6 className=' mb-2'>{t('detail_change_status_title')}</h6>
                    <div>
                      <Tag
                        label={t('detail_btn_reject')}
                        icon='circleX'
                        disabled={
                          isLoadingTaskApproved ||
                          effectiveStatusKey === 'rejected' ||
                          effectiveStatusKey === 'approved' ||
                          isSavingHtml ||
                          !approvalGuard.canApprove ||
                          !canChangeStatus
                        }
                        iconStroke='var(--error-500)'
                        onClick={() => setConfirmationMode('reject')}
                        color='danger'
                      />

                      <Tag
                        label={t('detail_btn_approve')}
                        icon='circleCheck'
                        disabled={
                          isLoadingTaskApproved ||
                          effectiveStatusKey === 'rejected' ||
                          effectiveStatusKey === 'approved' ||
                          isSavingHtml ||
                          !approvalGuard.canApprove ||
                          !canChangeStatus
                        }
                        iconStroke='var(--success-500)'
                        onClick={() => setConfirmationMode('approve')}
                        color='success'
                      />
                      {!approvalGuard.canApprove && approvalGuard.reason && (
                        <p className='mini color-warning-600 mt-2 mb-0'>
                          {approvalGuard.reason}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      <TaskConfirmation
        isOpen={!!confirmationMode}
        mode={confirmationMode === 'reject' ? 'reject' : 'approve'}
        onCancel={() => setConfirmationMode(null)}
        onConfirm={() => {
          if (!confirmationMode) {
            setConfirmationMode(null);
            return;
          }

          if (confirmationMode === 'reject') {
            handleStatusChange('rejected');
          }

          if (confirmationMode === 'approve') {
            if (shouldShowPjudWarning) {
              setShowSendWarning(true);
            } else {
              handleStatusChange('approved');
            }
          }
          setConfirmationMode(null);
        }}
      />
      {showSendWarning && (
        <Modal
          show={showSendWarning}
          onClose={closeModalConfirmate}
          title={
            <div className='d-flex align-items-center'>
              <h2 className='me-2'>{t('detail_modal_title')}</h2>
              <p className='task-id'>({value?.id})</p>
            </div>
          }
          width='1000px'
        >
          <div className='mt-4 p-3 border-top'>
            <p className='caption mb-3'>
              {t('detail_pjud_warning_before')}{' '}
              <strong>{t('detail_pjud_warning_bold')}</strong>.{' '}
              {t('detail_pjud_warning_question')}
            </p>

            <div className='d-flex justify-content-end'>
              <Button
                label={t('detail_btn_cancel')}
                size='sm'
                variant='secondary'
                className='me-2'
                disabled={isLoadingTaskApproved}
                onClick={() => setShowSendWarning(false)}
              />

              <Button
                label={
                  isLoadingTaskApproved ? t('detail_btn_sending') : t('detail_btn_send_doc')
                }
                size='sm'
                variant='success'
                disabled={isLoadingTaskApproved}
                onClick={() => handleStatusChange('approved')}
              />
            </div>
          </div>
        </Modal>
      )}
    </Modal>
  );
};

export default DetailTaskModal;
